import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { IconLoader2, IconPlus } from '@tabler/icons-react'
import { ForumSeo } from '@/components/ForumSeo'
import { ThreadListCard } from '@/components/forum/ThreadListCard'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'
import { apiFetch } from '@/lib/api'
import type { ThreadListItem } from '@/lib/types'
import { useAuthLoading, useConfirmedGuest, useSessionUser } from '@/lib/auth'
import { clientSiteOrigin } from '@/lib/siteOrigin'
import { websiteSearchJsonLd } from '~shared/jsonld-web'
import { DEFAULT_META_DESCRIPTION, DEFAULT_META_TITLE } from '~shared/seo'

export function DiscussionsPage() {
  const me = useSessionUser()
  const authLoading = useAuthLoading()
  const guest = useConfirmedGuest()
  const [params, setParams] = useSearchParams()

  const sort = (params.get('sort') as 'active' | 'new' | 'unanswered' | null) ?? 'active'
  const category = params.get('category') ?? ''
  const q = params.get('q') ?? ''
  const page = Number(params.get('page') ?? '1')

  const [threads, setThreads] = useState<ThreadListItem[]>([])
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    p.set('sort', sort)
    if (category) p.set('category', category)
    if (q) p.set('q', q)
    if (page > 1) p.set('page', String(page))
    return p.toString()
  }, [sort, category, q, page])

  useEffect(() => {
    let alive = true
    setLoading(true)

    void apiFetch<{ threads: ThreadListItem[]; totalPages: number }>(`/api/threads?${queryString}`)
      .then(payload => {
        if (!alive) return
        setThreads(payload.threads ?? [])
        setTotalPages(payload.totalPages ?? 1)
        setError(null)
      })
      .catch(() => {
        if (!alive) return
        setError('Could not load threads.')
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [queryString])

  const bumpPage = (next: number) => {
    const p = new URLSearchParams(params)
    if (next <= 1) p.delete('page')
    else p.set('page', String(next))
    setParams(p)
  }

  return (
    <div className="space-y-6">
      <ForumSeo
        title={DEFAULT_META_TITLE}
        description={DEFAULT_META_DESCRIPTION}
        jsonLd={websiteSearchJsonLd(clientSiteOrigin())}
      />
      <header className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm shadow-zinc-950/5">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-zinc-100 bg-gradient-to-br from-zinc-50 to-white px-5 py-5 md:px-6 md:py-6">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">North Kingstown • Community threads</p>
            <h1 className="mt-2 text-[1.75rem] font-semibold tracking-tight text-zinc-950 md:text-[2rem]">Discussions</h1>
            <p className="mt-2 max-w-[72ch] text-[15px] leading-relaxed text-zinc-600">
              Browse what neighbors are posting. Sort by activity, newest, or threads still waiting on a reply.
            </p>
          </div>

          <div className="flex flex-shrink-0 flex-col items-start gap-2 sm:items-end">
            <Link className={cn(!me && 'pointer-events-none opacity-40')} to="/new">
              <Button
                disabled={!me}
                className="gap-2 rounded-lg bg-sky-600 font-semibold text-white hover:bg-sky-500"
                size="md"
              >
                <IconPlus className="h-4 w-4" aria-hidden="true" />
                New topic
              </Button>
            </Link>
            {authLoading ? (
              <p className="text-[13px] font-medium text-zinc-500">Checking your session…</p>
            ) : guest ? (
              <p className="text-[13px] font-medium text-zinc-600">
                <Link className="font-semibold text-sky-700 no-underline" to="/sign-in">
                  Sign in
                </Link>{' '}
                to reply.
              </p>
            ) : null}
          </div>
        </div>
      </header>

      <section aria-labelledby="sort-label" className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm shadow-zinc-950/5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-100 bg-zinc-50/90 px-4 py-3 md:px-5">
          <h2 id="sort-label" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
            Sort threads
          </h2>

          <div
            role="radiogroup"

            aria-label="Thread sort options"
            className="flex flex-wrap gap-2 rounded-xl bg-white p-1 ring-1 ring-zinc-200/80"
          >
            {([
              ['active', 'Active'],
              ['new', 'New'],
              ['unanswered', 'Unanswered'],
            ] as const).map(([k, label]) => {
              const active = sort === k
              return (
                <button
                  key={k}
                  type="button"

                  role="radio"
                  aria-checked={active}
                  className={cn(
                    'rounded-lg px-3 py-2 text-[13px] font-semibold uppercase tracking-wide transition md:whitespace-nowrap',
                    active
                      ? 'bg-zinc-900 text-white shadow-sm'
                      : 'text-zinc-600 hover:bg-zinc-50',
                  )}
                  onClick={() => {
                    const next = new URLSearchParams(params)
                    next.set('sort', k)
                    next.delete('page')
                    setParams(next)
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        <div role="feed" aria-label="Forum threads">
          <div aria-live={loading ? 'polite' : 'off'} aria-busy={loading}>
            {loading ? (
              <div className="flex items-center gap-2 px-5 py-4 text-[14px] font-medium text-zinc-600">
                <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden="true" />
                Loading…
              </div>
            ) : null}
          </div>

          {error ? (
            <p className="border-b border-red-100 bg-red-50 px-5 py-3 text-[14px] font-medium text-red-900">{error}</p>
          ) : null}

          {!loading && !error && threads.length === 0 ? (
            <div className="px-5 py-12 md:px-8">
              <h3 className="text-xl font-semibold text-zinc-950">No threads yet</h3>
              <p className="mt-2 max-w-[60ch] text-[15px] leading-relaxed text-zinc-600">
                Categories are seeded. Start the first conversation with a descriptive title so neighbors can skim fast.
              </p>
              <div className="mt-6">
                <Link to="/new">
                  <Button variant="outline" className="rounded-lg font-semibold">

                    Start first topic
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            threads.map((thread, idx) => <ThreadListCard key={thread.id} thread={thread} isLastRow={idx === threads.length - 1} />)
          )}
        </div>
      </section>

      {totalPages > 1 ? (
        <nav
          aria-label="Thread list pagination"
          className="flex flex-wrap items-center gap-4 rounded-xl border border-zinc-200 bg-white px-4 py-3 text-[14px] font-medium text-zinc-700 shadow-sm shadow-zinc-950/5"
        >
          <PaginationBtn disabled={page <= 1} onClick={() => bumpPage(page - 1)}>
            Previous
          </PaginationBtn>

          <span aria-current={page}>
            Page{' '}
            <span className="font-semibold">{page}</span> /{' '}
            <span>{totalPages}</span>
          </span>

          <PaginationBtn disabled={page >= totalPages} onClick={() => bumpPage(page + 1)}>
            Next
          </PaginationBtn>
        </nav>
      ) : null}
    </div>
  )
}

function PaginationBtn(props: {
  disabled: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}

      className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-[13px] font-semibold text-zinc-800 shadow-sm transition hover:bg-zinc-50 disabled:opacity-35"
    >
      {props.children}
    </button>
  )
}
